// src/NavBar.js
import * as React from 'react';
// Remove useNavigate if not used elsewhere
import { useLocation, Link } from 'react-router-dom';
import AppBar from '@mui/material/AppBar';
import Box from '@mui/material/Box';
import Toolbar from '@mui/material/Toolbar';
import IconButton from '@mui/material/IconButton';
import Button from '@mui/material/Button';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import ToggleButton from '@mui/material/ToggleButton';
import ToggleButtonGroup from '@mui/material/ToggleButtonGroup';
import MenuIcon from '@mui/icons-material/Menu';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import VisibilityIcon from '@mui/icons-material/Visibility';
import PaletteIcon from '@mui/icons-material/Palette';
import WaterIcon from '@mui/icons-material/Water';
import BlurCircularIcon from '@mui/icons-material/BlurCircular';
import RocketLaunchIcon from '@mui/icons-material/RocketLaunch';
import SettingsInputComponentIcon from '@mui/icons-material/SettingsInputComponent';
import MemoryIcon from '@mui/icons-material/Memory';
import TrackChangesIcon from '@mui/icons-material/TrackChanges';
import BusinessIcon from '@mui/icons-material/Business';
import SettingsInputAntennaIcon from '@mui/icons-material/SettingsInputAntenna';
import RocketIcon from '@mui/icons-material/Rocket';
import NightsStayIcon from '@mui/icons-material/NightsStay';
import { useAppTheme } from './contexts/ThemeContext';
import { useBreakdownView } from './contexts/BreakdownViewContext';

// Define the pages shown in the NavBar.
// The Dev Fixtures entry is added only in dev builds — webpack dead-code-
// eliminates this branch in production so the link never ships.
const pages = [
  { label: 'Home', path: '/landing' },
  { label: 'Flight Breakdown', path: '/breakdown' }, // Main active page
  { label: 'Flights', path: '/flights' }, // Saved flights list
  { label: 'Dispatcher', path: '/seat-one' }, // Dispatcher seat 1 demo
  ...(process.env.NODE_ENV === 'development'
    ? [
        { label: 'Dev Fixtures', path: '/dev/fixtures', dev: true },
        { label: 'Solver Bench', path: '/dev/bench', dev: true },
      ]
    : []),
];

// Labels for nav links to render as disabled (greyed, not navigable).
// Currently empty — User Settings and Driver Demo are unlinked entirely
// (their routes still exist for direct URLs / the auth era).
const disabledPages = [];

// Guides served as HTML pages from public/guides (no PDF downloads —
// corporate web filters treat downloads with suspicion; plain pages on
// this domain sail). Regenerated by docs/user-guide/publish-guides.sh.
const GUIDE_LINKS = [
  { label: 'User Guide', href: '/guides/user-guide.html' },
  { label: 'Run Sheet Guide', href: '/guides/run-sheet-guide.html' },
  { label: 'Run Sheet Guide (B&W)', href: '/guides/run-sheet-guide-bw.html' },
];

// Per-theme navbar styling + menu metadata. Adding a theme = one entry here,
// its CSS file, and its registration in ThemeContext. Values for the original
// five themes are copied verbatim from the old conditional chains.
const THEME_NAV = [
  {
    id: 'default', label: 'Default', Icon: PaletteIcon,
    menuBg: 'rgba(249, 182, 18, 0.1)',
  },
  {
    id: 'nightvision', label: 'Night Vision', Icon: VisibilityIcon,
    accent: '#00ff00', hoverAccent: '#39ff14', hoverBg: 'rgba(0,255,0,0.1)',
    glow: 'drop-shadow(0 0 5px rgba(0, 255, 0, 0.8))',
    hoverGlow: 'drop-shadow(0 0 10px rgba(57, 255, 20, 1))',
    menuBg: 'rgba(0, 255, 0, 0.1)',
    logoFilter: 'brightness(0) saturate(100%) invert(68%) sepia(97%) saturate(7498%) hue-rotate(86deg) brightness(118%) contrast(119%) drop-shadow(0 0 10px rgba(0, 255, 0, 0.8))',
  },
  {
    id: 'submarine', label: 'Submarine Sonar', Icon: WaterIcon,
    accent: '#00FFAA', hoverAccent: '#00FFBB', hoverBg: 'rgba(0,255,170,0.1)',
    glow: 'drop-shadow(0 0 8px rgba(0, 255, 170, 0.6))',
    hoverGlow: 'drop-shadow(0 0 15px rgba(0, 255, 170, 1))',
    menuBg: 'rgba(0, 255, 170, 0.1)',
    logoFilter: 'brightness(0) saturate(100%) invert(69%) sepia(45%) saturate(2000%) hue-rotate(120deg) brightness(110%) contrast(105%) drop-shadow(0 0 8px rgba(0, 255, 170, 0.8))',
  },
  {
    id: 'bladerunner', label: 'Blade Runner', Icon: BlurCircularIcon, iconClass: 'theme-icon-br',
    accent: '#ffe066', hoverAccent: '#ffe39f', hoverBg: 'rgba(255,153,0,0.12)',
    glow: 'drop-shadow(0 0 10px rgba(255, 153, 0, 0.7))',
    hoverGlow: 'drop-shadow(0 0 15px rgba(255, 171, 0, 0.9))',
    menuBg: 'rgba(255, 153, 0, 0.12)',
    logoFilter: 'brightness(0) saturate(100%) invert(73%) sepia(26%) saturate(3200%) hue-rotate(342deg) brightness(104%) contrast(104%) drop-shadow(0 0 12px rgba(255, 153, 0, 0.8))',
  },
  {
    id: 'orbit', label: 'Orbital Command', Icon: RocketLaunchIcon, iconClass: 'theme-icon-orbit',
    accent: '#3ef5ff', hoverAccent: '#9dfdff', hoverBg: 'rgba(62,245,255,0.12)',
    glow: 'drop-shadow(0 0 10px rgba(62, 245, 255, 0.75))',
    hoverGlow: 'drop-shadow(0 0 18px rgba(62, 245, 255, 0.95))',
    menuBg: 'rgba(62, 245, 255, 0.12)',
    logoFilter: 'brightness(0) saturate(100%) invert(51%) sepia(57%) saturate(1200%) hue-rotate(162deg) brightness(108%) contrast(102%) drop-shadow(0 0 12px rgba(62, 245, 255, 0.85))',
  },
  {
    id: 'cyberpunk', label: 'Cyberpunk', Icon: MemoryIcon, iconClass: 'theme-icon-cyber',
    accent: '#00d4ff', hoverAccent: '#ff003c', hoverBg: 'rgba(0,212,255,0.15)',
    glow: 'drop-shadow(0 0 12px rgba(0, 212, 255, 0.9)) drop-shadow(0 0 20px rgba(255, 0, 60, 0.4))',
    hoverGlow: 'drop-shadow(0 0 20px rgba(0, 212, 255, 1)) drop-shadow(0 0 30px rgba(255, 0, 60, 0.6))',
    menuBg: 'rgba(0, 212, 255, 0.15)',
  },
  {
    id: 'overwatch', label: 'Overwatch', Icon: TrackChangesIcon,
    accent: '#9fd8ff', hoverAccent: '#d9efff', hoverBg: 'rgba(159,216,255,0.1)',
    // Matte by design — the fusion center doesn't glow
    menuBg: 'rgba(159, 216, 255, 0.12)',
    logoFilter: 'brightness(0) saturate(100%) invert(82%) sepia(14%) saturate(1100%) hue-rotate(177deg) brightness(103%) contrast(101%)',
  },
  {
    id: 'corporate', label: 'Corporate', Icon: BusinessIcon,
    accent: '#2563eb', hoverAccent: '#1d4ed8', hoverBg: 'rgba(37,99,235,0.08)',
    menuBg: 'rgba(37, 99, 235, 0.1)',
    // Light bar: render the logo in corporate blue
    logoFilter: 'brightness(0) saturate(100%) invert(33%) sepia(78%) saturate(1900%) hue-rotate(213deg) brightness(95%) contrast(96%)',
  },
  {
    id: 'blackbird', label: 'Blackbird', Icon: SettingsInputAntennaIcon,
    accent: '#ffb000', hoverAccent: '#ffd35c', hoverBg: 'rgba(255,176,0,0.12)',
    glow: 'drop-shadow(0 0 8px rgba(255, 176, 0, 0.8))',
    hoverGlow: 'drop-shadow(0 0 14px rgba(255, 176, 0, 1))',
    menuBg: 'rgba(255, 176, 0, 0.12)',
    logoFilter: 'brightness(0) saturate(100%) invert(72%) sepia(62%) saturate(1600%) hue-rotate(357deg) brightness(101%) contrast(103%) drop-shadow(0 0 10px rgba(255, 176, 0, 0.7))',
  },
  {
    id: 'apollo', label: 'Apollo', Icon: RocketIcon,
    accent: '#ece5d3', hoverAccent: '#fffaf0', hoverBg: 'rgba(236,229,211,0.1)',
    glow: 'drop-shadow(0 0 7px rgba(236, 229, 211, 0.7))',
    hoverGlow: 'drop-shadow(0 0 12px rgba(255, 250, 240, 0.9))',
    menuBg: 'rgba(30, 100, 208, 0.16)',
    // Incandescent warm white — launch-pad floodlights
    logoFilter: 'brightness(0) saturate(100%) invert(94%) sepia(8%) saturate(420%) hue-rotate(7deg) brightness(103%) contrast(94%) drop-shadow(0 0 9px rgba(236, 229, 211, 0.6))',
  },
  // VAULTED until Halloween 2026 — re-enable by uncommenting this entry
  // AND re-adding 'graveyard' to the three lists in ThemeContext.js
  // (validThemes, the toggle themes array, and the setTheme allowlist).
  // All theme code, CSS, and overlays remain in place, untouched.
  // {
  //   id: 'graveyard', label: 'Graveyard Shift', Icon: NightsStayIcon,
  //   accent: '#b8ffd9', hoverAccent: '#eafff2', hoverBg: 'rgba(184,255,217,0.08)',
  //   glow: 'drop-shadow(0 0 7px rgba(184, 255, 217, 0.6))',
  //   hoverGlow: 'drop-shadow(0 0 13px rgba(184, 255, 217, 0.85))',
  //   menuBg: 'rgba(184, 255, 217, 0.1)',
  //   logoFilter: 'brightness(0) saturate(100%) invert(92%) sepia(12%) saturate(420%) hue-rotate(90deg) brightness(102%) contrast(92%) drop-shadow(0 0 9px rgba(184, 255, 217, 0.55))',
  // },
];

const themeNavFor = (id) => THEME_NAV.find(t => t.id === id) || THEME_NAV[0];

function NavBar() {
  const location = useLocation();
  const { currentTheme, setTheme } = useAppTheme();
  const { viewMode, setViewMode, detailsOpen, setDetailsOpen } = useBreakdownView();
  const [anchorElNav, setAnchorElNav] = React.useState(null);
  const [anchorElGuides, setAnchorElGuides] = React.useState(null);
  const [anchorElTheme, setAnchorElTheme] = React.useState(null);

  const handleOpenNavMenu = (event) => {
    setAnchorElNav(event.currentTarget);
  };
  const handleCloseNavMenu = () => {
    setAnchorElNav(null);
  };

  const handleOpenThemeMenu = (event) => {
    setAnchorElTheme(event.currentTarget);
  };

  const handleCloseThemeMenu = () => {
    setAnchorElTheme(null);
  };

  const handleThemeSelect = (theme) => {
    setTheme(theme);
    handleCloseThemeMenu();
  };

  // No handleLogout needed

  const isActiveRoute = (path) => location.pathname === path;

  // --- Helper function for Desktop Button Styles ---
  const getDesktopButtonStyles = (page, isDisabled) => {
    const active = isActiveRoute(page.path);
    let styles = {
      fontWeight: 600, color: '#fff', cursor: 'pointer',
      '&:hover': { backgroundColor: 'rgba(255,255,255,0.1)', color: '#f9b612' },
    };
    if (active) styles = { ...styles, color: '#f9b612', fontWeight: 700 };
    if (isDisabled) styles = {
      ...styles, color: 'rgba(255, 255, 255, 0.5)', fontWeight: 600, cursor: 'not-allowed',
      '&:hover': { backgroundColor: 'transparent', color: 'rgba(255, 255, 255, 0.5)' },
    };
    if (page.dev) styles = {
      ...styles,
      color: active ? '#FF8A80' : '#FF5252',
      border: '1px dashed rgba(255, 82, 82, 0.5)',
      borderRadius: '4px',
      px: 1,
      '&:hover': { backgroundColor: 'rgba(255, 82, 82, 0.1)', color: '#FF8A80' },
    };
    return styles;
  };

  // --- Helper function for Mobile Button Styles ---
  const getMobileButtonStyles = (page, isDisabled) => {
    const active = isActiveRoute(page.path);
    let styles = {
      fontWeight: 400, color: '#000', width: '100%', justifyContent: 'flex-start', cursor: 'pointer',
      '&:hover': { color: '#f9b612', backgroundColor: 'rgba(0, 0, 0, 0.04)' },
    };
    if (active) styles = { ...styles, color: '#f9b612', fontWeight: 700 };
    if (isDisabled) styles = {
      ...styles, color: 'grey', fontWeight: 400, cursor: 'not-allowed',
      '&:hover': { backgroundColor: 'transparent', color: 'grey' },
    };
    return styles;
  };

  return (
    <AppBar
      position="fixed"
      sx={{
        top: 0,
        left: 0,
        right: 0,
        transform: 'none',
        width: '100%',
        maxWidth: 'none',
        borderRadius: 0,
        borderWidth: 0,
        borderStyle: 'none',
        borderBottom: '1px solid rgba(255, 255, 255, 0.1)',
        backgroundColor: 'rgba(30, 30, 30, 0.7)', // Frosted glass dark background
        backdropFilter: 'blur(10px)', // Stronger blur for glassmorphism
        WebkitBackdropFilter: 'blur(10px)', // Safari support
        boxShadow: 1,
        zIndex: (theme) => Math.max(theme.zIndex.drawer, theme.zIndex.modal) + 2,
        position: 'fixed',
      }}
    >
      <Toolbar disableGutters sx={{ px: 2 }}>
        {/* MOBILE: Hamburger Icon & Menu */}
        <Box sx={{ display: { xs: 'flex', md: 'none' }, mr: 1 }}> {/* Reduced margin */}
          <IconButton size="large" onClick={handleOpenNavMenu} color="inherit">
            <MenuIcon />
          </IconButton>
          <Menu
            anchorEl={anchorElNav}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
            transformOrigin={{ vertical: 'top', horizontal: 'left' }}
            open={Boolean(anchorElNav)}
            onClose={handleCloseNavMenu}
            sx={{ display: { xs: 'block', md: 'none' }, '& .MuiPaper-root': { marginTop: '8px' } }}
          >
            {pages.filter((page) => !page.dev).map((page) => {
              const isDisabled = disabledPages.includes(page.label); // Check against disabled list
              return (
                <MenuItem key={page.label} onClick={handleCloseNavMenu} disabled={isDisabled} sx={{ p: 0 }}>
                  <Button component={Link} to={page.path} disabled={isDisabled} sx={getMobileButtonStyles(page, isDisabled)}>
                    {page.label}
                  </Button>
                </MenuItem>
              );
            })}
            {GUIDE_LINKS.map((g) => (
              <MenuItem key={g.label} onClick={handleCloseNavMenu} sx={{ p: 0 }}>
                <Button component="a" href={g.href} target="_blank" rel="noopener" sx={getMobileButtonStyles(g, false)}>
                  {g.label}
                </Button>
              </MenuItem>
            ))}
            {/* Dev-only pages last, mirroring the desktop bar */}
            {pages.filter((page) => page.dev).map((page) => (
              <MenuItem key={page.label} onClick={handleCloseNavMenu} sx={{ p: 0 }}>
                <Button component={Link} to={page.path} sx={getMobileButtonStyles(page, false)}>
                  {page.label}
                </Button>
              </MenuItem>
            ))}
          </Menu>
        </Box>

        {/* Logo */}
        <Box sx={{ display: 'flex', alignItems: 'center', mr: 2 }}>
          <Box sx={{ ml: 1 }}>
            <img
              src="/FullLogo_Transparent_NoBuffer-2.png"
              alt="Denver Dispatch Logo"
              className="brand-logo"
              style={{
                height: '40px',
                filter: themeNavFor(currentTheme).logoFilter || 'none',
                transition: 'filter 0.3s ease'
              }}
            />
          </Box>
        </Box>

        {/* DESKTOP: Nav Links */}
        <Box sx={{ flexGrow: 1, display: { xs: 'none', md: 'flex' }, justifyContent: 'center', gap: { md: 2, lg: 3 } }}> {/* Adjusted gap */}
          {pages.filter((page) => !page.dev).map((page) => {
            const isDisabled = disabledPages.includes(page.label); // Check against disabled list
            // State classes let light themes restyle link states from CSS
            // (inline sx has no hook for "active" otherwise).
            const linkClass = [
              'nav-link',
              isActiveRoute(page.path) && 'nav-link--active',
              page.dev && 'nav-link--dev',
              isDisabled && 'nav-link--disabled',
            ].filter(Boolean).join(' ');
            return (
              <Button key={page.label} className={linkClass} component={Link} to={page.path} disabled={isDisabled} sx={getDesktopButtonStyles(page, isDisabled)}>
                {page.label}
              </Button>
            );
          })}
          <Button
            className="nav-link"
            onClick={(e) => setAnchorElGuides(e.currentTarget)}
            sx={{
              fontWeight: 600, color: '#fff',
              '&:hover': { backgroundColor: 'rgba(255,255,255,0.1)', color: '#f9b612' },
            }}
          >
            Help
          </Button>
          <Menu
            anchorEl={anchorElGuides}
            open={Boolean(anchorElGuides)}
            onClose={() => setAnchorElGuides(null)}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
            transformOrigin={{ vertical: 'top', horizontal: 'left' }}
            sx={{ '& .MuiPaper-root': { marginTop: '8px' } }}
          >
            {GUIDE_LINKS.map((g) => (
              <MenuItem
                key={g.label}
                component="a"
                href={g.href}
                target="_blank"
                rel="noopener"
                onClick={() => setAnchorElGuides(null)}
              >
                {g.label}
              </MenuItem>
            ))}
          </Menu>
          {/* Dev-only links sit at the end of the bar, after Help */}
          {pages.filter((page) => page.dev).map((page) => {
            const isDisabled = disabledPages.includes(page.label); // Check against disabled list
            // State classes let light themes restyle link states from CSS
            // (inline sx has no hook for "active" otherwise).
            const linkClass = [
              'nav-link',
              isActiveRoute(page.path) && 'nav-link--active',
              page.dev && 'nav-link--dev',
              isDisabled && 'nav-link--disabled',
            ].filter(Boolean).join(' ');
            return (
              <Button key={page.label} className={linkClass} component={Link} to={page.path} disabled={isDisabled} sx={getDesktopButtonStyles(page, isDisabled)}>
                {page.label}
              </Button>
            );
          })}
        </Box>

        {/* RIGHT SIDE: Breakdown view toggle (this page only) + Theme Dropdown */}
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          {location.pathname === '/flight-data' && (
            <ToggleButtonGroup
              size="small"
              exclusive
              value={viewMode}
              onChange={(e, mode) => setViewMode(mode)}
              sx={{
                mr: 1.5,
                '& .MuiToggleButton-root': {
                  color: 'rgba(255,255,255,0.7)',
                  borderColor: 'rgba(255,255,255,0.25)',
                  py: 0.25,
                  px: 1.25,
                  fontSize: '0.7rem',
                  fontWeight: 600,
                  lineHeight: 1.4,
                },
                '& .Mui-selected': {
                  color: '#1a1a1a !important',
                  backgroundColor: '#f9b612 !important',
                },
                '& .Mui-selected:hover': { backgroundColor: '#ffc83d !important' },
              }}
            >
              <ToggleButton value="classic">Classic</ToggleButton>
              <ToggleButton value="focus">Focus</ToggleButton>
            </ToggleButtonGroup>
          )}
          {/* Focus-mode details drawer toggle — in-flow here so it can never
              collide with theme chrome or float over open menus (it used to
              be a fixed folder tab hanging under the bar). */}
          {location.pathname === '/flight-data' && viewMode === 'focus' && (
            <Button
              size="small"
              className={detailsOpen ? 'details-toggle details-toggle--open' : 'details-toggle'}
              onClick={() => setDetailsOpen(!detailsOpen)}
              startIcon={detailsOpen
                ? <ExpandLessIcon sx={{ fontSize: '1rem !important' }} />
                : <ExpandMoreIcon sx={{ fontSize: '1rem !important' }} />}
              sx={{
                mr: 1.5,
                py: 0.25,
                px: 1.25,
                fontSize: '0.7rem',
                fontWeight: 600,
                lineHeight: 1.4,
                letterSpacing: 'inherit',
                color: detailsOpen ? '#1a1a1a' : 'rgba(255,255,255,0.7)',
                backgroundColor: detailsOpen ? '#f9b612' : 'transparent',
                border: '1px solid rgba(255,255,255,0.25)',
                '&:hover': {
                  backgroundColor: detailsOpen ? '#ffc83d' : 'rgba(255,255,255,0.08)',
                },
              }}
            >
              Details
            </Button>
          )}
          {currentTheme === 'default' ? (
            <Button
              onClick={handleOpenThemeMenu}
              sx={{
                color: '#fff',
                fontWeight: 600,
                '&:hover': {
                  backgroundColor: 'rgba(255,255,255,0.1)',
                  color: '#f9b612'
                }
              }}
            >
              Themes
            </Button>
          ) : (
            (() => {
              const t = themeNavFor(currentTheme);
              const ThemeIcon = t.Icon;
              return (
                <IconButton
                  onClick={handleOpenThemeMenu}
                  sx={{
                    color: t.accent || '#fff',
                    filter: t.glow || 'none',
                    '&:hover': {
                      backgroundColor: t.hoverBg || 'rgba(255,255,255,0.1)',
                      color: t.hoverAccent || '#fff',
                      filter: t.hoverGlow || 'none',
                    },
                  }}
                >
                  <ThemeIcon className={t.iconClass} />
                </IconButton>
              );
            })()
          )}
          <Menu
            anchorEl={anchorElTheme}
            open={Boolean(anchorElTheme)}
            onClose={handleCloseThemeMenu}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
            transformOrigin={{ vertical: 'top', horizontal: 'right' }}
            sx={{
              '& .MuiPaper-root': {
                marginTop: '8px'
              }
            }}
          >
            {THEME_NAV.map((t) => {
              const ItemIcon = t.Icon;
              return (
                <MenuItem
                  key={t.id}
                  onClick={() => handleThemeSelect(t.id)}
                  selected={currentTheme === t.id}
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1.5,
                    '&.Mui-selected': {
                      backgroundColor: t.menuBg
                    }
                  }}
                >
                  <ItemIcon fontSize="small" sx={{ color: currentTheme === t.id && t.accent ? t.accent : 'inherit' }} />
                  <span>{t.label}</span>
                </MenuItem>
              );
            })}
          </Menu>
        </Box>

      </Toolbar>
    </AppBar>
  );
}

export default NavBar;
